/**
 * Resibo demo — data model and helpers, ported from the design mock
 * (public/resibo-pixelmojo.html). The mock is the literal spec: the sample
 * records, the validation messages and the number/date formatting below are
 * its own, unchanged.
 *
 * This edition is deliberately offline. Nothing here touches the network, as
 * the mock's own help copy states: "This offline edition previews files and
 * lets you enter records manually. It does not run AI extraction." The demo
 * extraction API this page once called was removed along with it.
 */

export type LineItem = { name: string; qty: number; price: number }

export type Receipt = {
  id: string
  merchant: string
  date: string
  category: string
  currency: string
  total: number | null
  tax: number
  taxLabel: string
  filename: string
  sample: boolean
  items: LineItem[]
  receiptNumber?: string
  address?: string
  /** Local-file records only — object URL, MIME type and size for the preview. */
  url?: string
  type?: string
  size?: number
}

export const CATEGORIES = [
  'Meals & drinks',
  'Transport',
  'Office supplies',
  'Software',
  'Travel',
  'Other',
] as const

export const CURRENCIES = [
  { code: 'PHP', label: 'PHP · Philippine peso' },
  { code: 'SGD', label: 'SGD · Singapore dollar' },
  { code: 'USD', label: 'USD · US dollar' },
] as const

export const CURRENCY_NAMES: Record<string, string> = {
  PHP: 'Philippine peso',
  SGD: 'Singapore dollar',
  USD: 'US dollar',
}

export const TAX_LABELS = [
  'VAT included',
  'GST included',
  'Sales tax included',
  'No tax recorded',
] as const

export const SUPPORTED_TYPES: Record<string, true> = {
  'image/jpeg': true,
  'image/png': true,
  'image/webp': true,
  'application/pdf': true,
}

export const EXTENSION_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  pdf: 'application/pdf',
}

export const MAX_FILE_BYTES = 10 * 1024 * 1024
export const MAX_SESSION_BYTES = 100 * 1024 * 1024
export const MAX_RECORDS = 50

export const sampleData: Receipt[] = [
  {
    id: 'sample-1',
    merchant: 'Yardstick Coffee',
    date: '2026-09-07',
    category: 'Meals & drinks',
    currency: 'PHP',
    total: 785,
    tax: 84.11,
    taxLabel: 'VAT included',
    filename: 'yardstick-receipt.jpg',
    sample: true,
    receiptNumber: 'YS-0907-0148',
    address: '106 Esteban Street, Legazpi Village\nMakati City, Philippines',
    items: [
      { name: 'Flat white', qty: 2, price: 190 },
      { name: 'Iced black', qty: 1, price: 175 },
      { name: 'Almond croissant', qty: 1, price: 230 },
    ],
  },
  {
    id: 'sample-2',
    merchant: 'Grab',
    date: '2026-09-06',
    category: 'Transport',
    currency: 'PHP',
    total: 420,
    tax: 0,
    taxLabel: 'No tax recorded',
    filename: 'grab-trip.pdf',
    sample: true,
    receiptNumber: 'GR-0906-0276',
    address: 'Trip receipt\nMakati to Bonifacio Global City',
    items: [
      { name: 'Trip fare', qty: 1, price: 365 },
      { name: 'Booking fee', qty: 1, price: 55 },
    ],
  },
  {
    id: 'sample-3',
    merchant: 'Common Room',
    date: '2026-09-05',
    category: '',
    currency: 'PHP',
    total: 2490,
    tax: 266.79,
    taxLabel: 'VAT included',
    filename: 'common-room-receipt.jpg',
    sample: true,
    receiptNumber: 'CR-0905-0082',
    address: 'Power Plant Mall, Rockwell\nMakati City, Philippines',
    items: [
      { name: 'Desk notebook', qty: 3, price: 450 },
      { name: 'Weekly planner', qty: 2, price: 570 },
    ],
  },
]

/** Pristine copies of the samples — the source receipt keeps printing the
 *  original even after the record beside it is edited. */
export const sourceCopies = new Map<string, Receipt>(
  sampleData.map(r => [r.id, structuredClone(r)])
)

export const cloneSamples = (): Receipt[] => structuredClone(sampleData)

export const cents = (value: number) => Math.round(Number(value) * 100)

export const money = (amount: number | null, currency = 'PHP', code = false) =>
  new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency,
    currencyDisplay: code ? 'code' : 'narrowSymbol',
    minimumFractionDigits: 2,
  }).format(amount || 0)

export const decimal = (amount: number) =>
  Number(amount || 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

export const shortDate = (date: string) =>
  date
    ? new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
      })
    : 'No date'

export const longDate = (date: string) =>
  date
    ? new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : 'Not entered'

/** Everything standing between a record and "ready". Order is the mock's. */
export function issues(r: Receipt): string[] {
  const problems: string[] = []
  if (!r.merchant.trim()) problems.push('Add the merchant name.')
  if (!r.date) problems.push('Add the receipt date.')
  if (!r.category) problems.push('Choose a category to complete this record.')
  if (r.total === null || !Number.isFinite(r.total) || r.total < 0)
    problems.push('Enter the receipt total.')
  if (r.items.length === 0) problems.push('Add at least one line item.')
  if (r.items.length && r.total !== null) {
    const sum = r.items.reduce((n, i) => n + cents(i.price) * i.qty, 0)
    const diff = sum - cents(r.total)
    if (Math.abs(diff) > 1)
      problems.push(
        `Line items ${diff > 0 ? 'exceed' : 'are below'} the receipt total by ${money(
          Math.abs(diff) / 100,
          r.currency
        )}.`
      )
  }
  if (r.total !== null && r.tax > r.total)
    problems.push('Tax cannot exceed the receipt total.')
  return problems
}

export const ready = (r: Receipt) => issues(r).length === 0

/** Guards a CSV cell against spreadsheet formula injection. */
export function safeCsv(value: unknown): string {
  let s = String(value ?? '')
  if (/^[\s]*[=+@-]/.test(s) || /^[\t\r]/.test(s)) s = `'${s}`
  return `"${s.replace(/"/g, '""')}"`
}

/** The local, heuristic answer engine behind "Ask this session". No AI service
 *  is connected — the mock answers from the records in memory. */
export function answerQuery(query: string, records: Receipt[]): string {
  const q = query.toLowerCase().trim()

  if (/review|missing|incomplete|attention/.test(q)) {
    const rs = records.filter(r => !ready(r))
    return rs.length
      ? rs
          .map(r => `${r.merchant || r.filename}: ${issues(r).join(' ')}`)
          .join('\n\n')
      : 'All records are ready. Nothing needs review.'
  }

  const categoryMap: Array<[RegExp, string]> = [
    [/meal|coffee|drink|food/, 'Meals & drinks'],
    [/transport|ride|grab/, 'Transport'],
    [/office|supplies|stationery/, 'Office supplies'],
    [/software|subscription/, 'Software'],
    [/travel|flight|hotel/, 'Travel'],
  ]
  const category = categoryMap.find(([re]) => re.test(q))?.[1]
  const merchant = records.find(
    r => r.merchant && q.includes(r.merchant.toLowerCase())
  )
  const selectedCurrency = ['PHP', 'SGD', 'USD'].find(c =>
    new RegExp(`\\b${c.toLowerCase()}\\b`).test(q)
  )

  if (
    category ||
    merchant ||
    /total|spend|spent|amount|how much|sum|receipt|record|ready/.test(q)
  ) {
    const rs = records.filter(
      r =>
        (!merchant || r.merchant === merchant.merchant) &&
        (!category || merchant || r.category === category) &&
        (!selectedCurrency || r.currency === selectedCurrency) &&
        (!/ready/.test(q) || ready(r))
    )
    if (!rs.length) return 'No matching records in this session.'
    const grouped: Record<string, number> = {}
    rs.forEach(r => {
      grouped[r.currency] = (grouped[r.currency] || 0) + cents(r.total || 0)
    })
    return (
      `${rs.length} receipt${rs.length === 1 ? '' : 's'}` +
      `${merchant ? ` from ${merchant.merchant}` : category ? ` in ${category}` : ''}.\n` +
      `${Object.entries(grouped)
        .map(([c, t]) => money(t / 100, c, true))
        .join('\n')} recorded.\n` +
      `${rs.filter(r => !ready(r)).length} need review. Totals include entered amounts on unfinished records.`
    )
  }

  return 'Try a question about total spend, a merchant, meals, transport, office supplies, a currency, or receipts needing review.'
}
